import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sun,
  CloudSun,
  Cloud,
  CloudRain,
  CloudLightning,
  Waves,
  Thermometer,
  Sunrise as SunriseIcon,
  Sunset as SunsetIcon,
  Umbrella,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Open-Meteo WMO weather codes → coarse buckets we translate on the frontend.
const codeToCondition = (code) => {
  if (code == null) return "sunny";
  if (code === 0) return "sunny";
  if (code <= 2) return "mostlySunny";
  if (code === 3) return "partlyCloudy";
  if (code <= 48) return "cloudy";
  if (code <= 67) return "rainy";
  if (code <= 82) return "rainy";
  if (code <= 99) return "stormy";
  return "cloudy";
};

const conditionIcon = (condition) => {
  switch (condition) {
    case "sunny":
      return <Sun className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    case "mostlySunny":
      return <CloudSun className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    case "partlyCloudy":
      return <CloudSun className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    case "cloudy":
      return <Cloud className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    case "rainy":
      return <CloudRain className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    case "stormy":
      return <CloudLightning className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
    default:
      return <Sun className="w-8 h-8 sm:w-10 sm:h-10" aria-hidden="true" />;
  }
};

const formatTime = (iso, locale) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const langToLocale = (lang) =>
  ({ en: "en-GB", fr: "fr-FR", el: "el-GR", pl: "pl-PL" }[lang] || "en-GB");

export const WeatherWidget = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(`${API_BASE}/api/weather/wedding`);
        if (!res.ok) throw new Error("weather fetch failed");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchWeather();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="surface-card p-6 sm:p-8 animate-pulse min-h-[220px]"
        data-testid="weather-loading"
        aria-busy="true"
      >
        <div className="h-3 w-24 bg-foreground/10 rounded" />
        <div className="mt-6 h-8 w-40 bg-foreground/10 rounded" />
        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="h-16 bg-foreground/5 rounded" />
          <div className="h-16 bg-foreground/5 rounded" />
          <div className="h-16 bg-foreground/5 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return null; // Fail silently — a weather widget is nice-to-have, not critical.
  }

  const isForecast = data.mode === "forecast";
  const condition = isForecast ? codeToCondition(data.weather_code) : (data.condition || "sunny");
  const conditionLabel = t(`home.weather${condition.charAt(0).toUpperCase() + condition.slice(1)}`);
  const locale = langToLocale(lang);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      className="surface-card p-6 sm:p-8"
      data-testid="weather-widget"
      aria-label={t("home.weatherOverline")}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="editorial-kicker text-accent" data-testid="weather-mode">
            {isForecast ? t("home.weatherForecastTitle") : t("home.weatherHistoricalTitle")}
          </p>
          <p
            className="mt-2 font-serif-display italic text-2xl sm:text-3xl leading-snug"
            data-testid="weather-condition-label"
          >
            {conditionLabel}
          </p>
          <p className="mt-2 text-[12px] tracking-[0.18em] uppercase text-foreground/55">
            {t("home.weatherLocation")}
          </p>
        </div>
        <div
          className="shrink-0 text-accent flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-accent/10"
          data-testid="weather-icon"
        >
          {conditionIcon(condition)}
        </div>
      </div>

      {/* Temperature row */}
      <div
        className="mt-6 flex items-baseline gap-3 sm:gap-5 border-t border-foreground/15 pt-5"
        data-testid="weather-temps"
      >
        <div className="flex items-baseline gap-2">
          <span
            className="font-serif-display text-5xl sm:text-6xl leading-none tabular-nums"
            data-testid="weather-temp-high"
          >
            {data.temp_high_c}
          </span>
          <span className="font-serif-display text-2xl sm:text-3xl text-foreground/60">°</span>
        </div>
        <div className="flex items-center gap-1 text-foreground/55 text-[12px] tracking-[0.18em] uppercase">
          <span>{t("home.weatherHigh")}</span>
          <span className="mx-1">·</span>
          <span>{t("home.weatherLow")}</span>
          <span
            className="font-serif-display text-lg sm:text-xl text-foreground/85 tabular-nums ml-1"
            data-testid="weather-temp-low"
          >
            {data.temp_low_c}°
          </span>
        </div>
      </div>

      {/* Detail grid */}
      <div
        className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5"
        data-testid="weather-details"
      >
        {typeof data.sea_temp_c === "number" && (
          <div className="flex flex-col gap-1" data-testid="weather-sea">
            <div className="flex items-center gap-2 text-foreground/60">
              <Waves className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherSea")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">{data.sea_temp_c}°</span>
          </div>
        )}

        {typeof data.sunshine_hours === "number" && (
          <div className="flex flex-col gap-1" data-testid="weather-sunshine">
            <div className="flex items-center gap-2 text-foreground/60">
              <Sun className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherSunshine")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">{data.sunshine_hours}h</span>
          </div>
        )}

        {typeof data.precipitation_mm === "number" && (
          <div className="flex flex-col gap-1" data-testid="weather-rain">
            <div className="flex items-center gap-2 text-foreground/60">
              <Umbrella className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherRain")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">{data.precipitation_mm}mm</span>
          </div>
        )}

        {isForecast && data.sunrise && (
          <div className="flex flex-col gap-1" data-testid="weather-sunrise">
            <div className="flex items-center gap-2 text-foreground/60">
              <SunriseIcon className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherSunrise")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">
              {formatTime(data.sunrise, locale)}
            </span>
          </div>
        )}

        {isForecast && data.sunset && (
          <div className="flex flex-col gap-1" data-testid="weather-sunset">
            <div className="flex items-center gap-2 text-foreground/60">
              <SunsetIcon className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherSunset")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">
              {formatTime(data.sunset, locale)}
            </span>
          </div>
        )}

        {isForecast && typeof data.uv_max === "number" && (
          <div className="flex flex-col gap-1" data-testid="weather-uv">
            <div className="flex items-center gap-2 text-foreground/60">
              <Thermometer className="w-4 h-4" aria-hidden="true" />
              <span className="editorial-kicker">{t("home.weatherUV")}</span>
            </div>
            <span className="font-serif-display text-xl tabular-nums">{Math.round(data.uv_max)}</span>
          </div>
        )}
      </div>

      {/* Note */}
      <p
        className="mt-6 pt-4 border-t border-foreground/10 text-[11px] tracking-[0.18em] uppercase text-foreground/45"
        data-testid="weather-note"
      >
        {isForecast ? t("home.weatherForecastNote") : t("home.weatherHistoricalNote")}
      </p>
    </motion.div>
  );
};

export default WeatherWidget;
